// ./src/hooks/useEmbeddings.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  Embedding,
  EmbeddingService,
  EmbeddingFilter,
  EmbeddingInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseEmbeddingsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<EmbeddingFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;

  userId?: string | null;
}

export interface UseEmbeddingsReturn {
  // Data state
  embedding: Embedding | null;
  embeddings: Embedding[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<EmbeddingFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingEmbeddingId: string | null;
  selectedEmbedding: Embedding | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<EmbeddingFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<Embedding[]>;
  create: (params: EmbeddingInput) => Promise<Embedding | null>;
  update: (id: string, params: EmbeddingInput) => Promise<Embedding | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Search and filter operations
  setFilters: (filters: Partial<EmbeddingFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedEmbedding: (embedding: Embedding | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  embeddingsByLayoutType: Record<string, Embedding[]>;
  embeddingsByVenue: Record<string, Embedding[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useEmbeddings = (
  options: UseEmbeddingsOptions = {}
): UseEmbeddingsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [embedding, setEmbedding] = useState<Embedding | null>(null);
  const [embeddings, setEmbeddings] = useState<Embedding[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<EmbeddingFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingEmbeddingId, setRemovingEmbeddingId] = useState<string | null>(
    null
  );
  const [selectedEmbedding, setSelectedEmbedding] = useState<Embedding | null>(
    null
  );

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => embeddings.length === 0 && !loading,
    [embeddings.length, loading]
  );

  const embeddingsByLayoutType = useMemo(() => {
    return embeddings.reduce((acc, embedding) => {
      const layoutType = embedding.layoutType || "unknown";
      if (!acc[layoutType]) {
        acc[layoutType] = [];
      }
      acc[layoutType].push(embedding);
      return acc;
    }, {} as Record<string, Embedding[]>);
  }, [embeddings]);

  const embeddingsByVenue = useMemo(() => {
    return embeddings.reduce((acc, embedding) => {
      const venueId = embedding.venueId || "unknown";
      if (!acc[venueId]) {
        acc[venueId] = [];
      }
      acc[venueId].push(embedding);
      return acc;
    }, {} as Record<string, Embedding[]>);
  }, [embeddings]);

  // Helper function to update embedding in list if it exists, or add it if not
  const updateOrAddEmbeddingInList = useCallback(
    (updatedEmbedding: Embedding) => {
      setEmbeddings((prev) => {
        const existingIndex = prev.findIndex(
          (e) => e.id === updatedEmbedding.id
        );
        if (existingIndex >= 0) {
          const newEmbeddings = [...prev];
          newEmbeddings[existingIndex] = updatedEmbedding;
          return newEmbeddings;
        } else {
          return [updatedEmbedding, ...prev];
        }
      });
    },
    []
  );

  // Helper function to remove embedding from list if it exists
  const removeEmbeddingFromList = useCallback((embeddingId: string) => {
    setEmbeddings((prev) => prev.filter((e) => e.id !== embeddingId));
  }, []);

  // Helper function to remove multiple embeddings from list
  const removeEmbeddingsFromList = useCallback((embeddingIds: string[]) => {
    const idsSet = new Set(embeddingIds);
    setEmbeddings((prev) => prev.filter((e) => !idsSet.has(e.id || "")));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setEmbedding(null);
    setEmbeddings([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingEmbeddingId(null);
    setSelectedEmbedding(null);
  }, []);

  // Get single embedding (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await EmbeddingService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const embeddingData = result.data[0] as Embedding;
          setEmbedding(embeddingData);
          updateOrAddEmbeddingInList(embeddingData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load embedding";
          setLastError(errorMessage);
          setEmbedding(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load embedding";
        setLastError(errorMessage);
        setEmbedding(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddEmbeddingInList]
  );

  // List embeddings (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<EmbeddingFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setEmbeddings([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await EmbeddingService.list(
          filter as EmbeddingFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newEmbeddings = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setEmbeddings((prev) => {
              const existingIds = new Set(prev.map((e) => e.id));
              const uniqueNewEmbeddings = newEmbeddings.filter(
                (e: any) => !existingIds.has(e.id)
              );
              return [...prev, ...uniqueNewEmbeddings];
            });
          } else {
            setEmbeddings(newEmbeddings);
          }

          setHasMore(newEmbeddings.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load embeddings";
          setLastError(errorMessage);
          if (!isAppending) setEmbeddings([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load embeddings";
        setLastError(errorMessage);
        if (!isAppending) setEmbeddings([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many embeddings by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<Embedding[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await EmbeddingService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedEmbeddings = Array.isArray(result.data)
            ? result.data
            : [];
          loadedEmbeddings.forEach((embedding: any) => {
            updateOrAddEmbeddingInList(embedding);
          });
          setLastError(null);
          return loadedEmbeddings;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load embeddings";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load embeddings";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddEmbeddingInList]
  );

  // Create embedding (matching API method name)
  const create = useCallback(
    async (params: EmbeddingInput): Promise<Embedding | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await EmbeddingService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newEmbedding = result.data[0] as Embedding;
          setEmbedding(newEmbedding);
          setEmbeddings((prev) => [newEmbedding, ...prev]);
          setLastError(null);
          return newEmbedding;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create embedding";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create embedding";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update embedding (matching API method name)
  const update = useCallback(
    async (id: string, params: EmbeddingInput): Promise<Embedding | null> => {
      if (!id) {
        setLastError("Embedding ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await EmbeddingService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedEmbedding = result.data[0] as Embedding;
          setEmbedding(updatedEmbedding);
          updateOrAddEmbeddingInList(updatedEmbedding);
          setLastError(null);
          return updatedEmbedding;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update embedding";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update embedding";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddEmbeddingInList]
  );

  // Remove embedding (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Embedding ID is required for removal");
        return false;
      }

      setRemovingEmbeddingId(id);
      setLastError(null);

      try {
        const result = await EmbeddingService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setEmbedding(null);
          removeEmbeddingFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove embedding";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove embedding";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingEmbeddingId(null);
      }
    },
    [removeEmbeddingFromList]
  );

  // Remove many embeddings (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("At least one Embedding ID is required for removal");
        return false;
      }

      setLastError(null);

      try {
        const result = await EmbeddingService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setEmbedding(null);
          removeEmbeddingsFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove embeddings";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove embeddings";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeEmbeddingsFromList]
  );

  // Load more embeddings
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh embeddings
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && isAuthenticated && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, isAuthenticated, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    embedding,
    embeddings,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingEmbeddingId,
    selectedEmbedding,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedEmbedding,
    reset,
    embeddingsByLayoutType,
    embeddingsByVenue,
    lastError,
    clearError,
  };
};
